import { useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import SiteHeader from '../layout/SiteHeader';
import SiteFooter from '../layout/SiteFooter';
import HomePage from '../pages/home/HomePage';
import ModulePage from '../pages/modules/ModulePage';
import ContactPage from '../pages/modules/ContactPage';
import NotFoundPage from '../pages/modules/NotFoundPage';
import { getModule, modules } from '../content/modules';
import { brand } from '../content/site';
import {
  getMessages,
  hasLocalePrefix,
  localeFromPath,
  localizedPath,
  pathWithoutLocale,
} from '../i18n/locale';

export default function App() {
  const location = useLocation();
  const locale = localeFromPath(location.pathname);
  const messages = getMessages(locale);
  const pagePath = pathWithoutLocale(location.pathname);
  const previousPath = useRef(location.pathname);
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem('junoyi-theme') || 'light';
    } catch {
      return 'light';
    }
  });

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem('junoyi-theme', theme);
    } catch {}
  }, [theme]);

  useEffect(() => {
    document.documentElement.lang = messages.meta.htmlLang;
    if (previousPath.current !== location.pathname) {
      window.scrollTo(0, 0);
      previousPath.current = location.pathname;
    }
    const moduleKey = Object.keys(modules).find((key) => modules[key].path === pagePath);
    const module = moduleKey ? getModule(moduleKey, locale) : null;
    document.title =
      pagePath === '/'
        ? `${brand.name} — Build. Share. Evolve.`
        : `${module?.name || (pagePath === '/company/contact' ? messages.contactPage.name : messages.notFound.name)} · ${brand.name}`;
  }, [location.pathname, locale, pagePath, messages]);

  if (!hasLocalePrefix(location.pathname)) {
    return (
      <Navigate
        to={`${localizedPath('zh', location.pathname)}${location.search}${location.hash}`}
        replace
      />
    );
  }

  return (
    <div className="site" id="top">
      <SiteHeader
        theme={theme}
        onThemeToggle={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
      />
      <Routes>
        <Route path="/:locale" element={<HomePage theme={theme} />} />
        {Object.entries(modules).map(([key, module]) => (
          <Route
            key={key}
            path={`/:locale${module.path}`}
            element={<ModulePage module={getModule(key, locale)} />}
          />
        ))}
        <Route path="/:locale/company/contact" element={<ContactPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <SiteFooter />
    </div>
  );
}
